import * as React from "react";
import { useParams } from "react-router";
import { useCurrentProject } from "@/lib/project";
import { useQuery } from "@tanstack/react-query";
import { Download, ChevronDown, Search, Filter } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { PageHeader } from "@/components/bespoke";
import { formatDateTime } from "@/lib/format";

export type EvalResult = {
  id: string;
  projectId: string;
  runId: string;
  datasetItemId: string;
  criterionId: string | null;
  score: number | null;
  passed: boolean | null;
  details: Record<string, unknown>;
  gradedAt: string;
};

async function fetchResults(projectId: string, runId: string): Promise<EvalResult[]> {
  const response = await fetch(`/api/v1/projects/${projectId}/eval/runs/${runId}/results`, {
    method: "GET",
    credentials: "same-origin",
  });
  if (!response.ok) return [];
  return (await response.json()).results;
}

async function fetchRun(projectId: string, runId: string) {
  const response = await fetch(`/api/v1/projects/${projectId}/eval/runs/${runId}`, {
    method: "GET",
    credentials: "same-origin",
  });
  if (!response.ok) return null;
  return response.json();
}

function getPassedBadge(passed: boolean | null) {
  if (passed === null) return <Badge variant="outline">Pending</Badge>;
  return passed ? (
    <Badge variant="success">Passed</Badge>
  ) : (
    <Badge variant="destructive">Failed</Badge>
  );
}

function ResultRow({ result }: { result: EvalResult }) {
  return (
    <tr className="group hover:bg-muted/50">
      <td className="border-b px-3 py-2.5 align-middle whitespace-nowrap group-last:border-b-0 first:pl-5 last:pr-5 font-mono text-sm">
        {result.datasetItemId.slice(0, 8)}…
      </td>
      <td className="border-b px-3 py-2.5 align-middle whitespace-nowrap group-last:border-b-0 first:pl-5 last:pr-5">
        {result.criterionId ? result.criterionId.slice(0, 8) + "…" : "Overall"}
      </td>
      <td className="border-b px-3 py-2.5 align-middle whitespace-nowrap group-last:border-b-0 first:pl-5 last:pr-5 tabular-nums">
        {result.score !== null ? result.score.toFixed(3) : "—"}
      </td>
      <td className="border-b px-3 py-2.5 align-middle whitespace-nowrap group-last:border-b-0 first:pl-5 last:pr-5">
        {getPassedBadge(result.passed)}
      </td>
      <td className="border-b px-3 py-2.5 align-middle whitespace-nowrap group-last:border-b-0 first:pl-5 last:pr-5 text-muted-foreground max-w-xs truncate font-mono text-xs">
        {JSON.stringify(result.details).slice(0, 80)}
      </td>
      <td className="border-b px-3 py-2.5 align-middle whitespace-nowrap group-last:border-b-0 first:pl-5 last:pr-5 text-muted-foreground">
        {formatDateTime(result.gradedAt)}
      </td>
    </tr>
  );
}

export function EvalResultsPage({ headingLevel = "h1" }: { headingLevel?: "h1" | "h2" }) {
  const { project } = useCurrentProject();
  const projectId = project?.id;
  const { runId } = useParams<{ runId: string }>();

  const { data: run, isLoading: runLoading } = useQuery({
    queryKey: ["eval-run", projectId, runId],
    queryFn: () => fetchRun(projectId!, runId!),
    enabled: !!projectId && !!runId,
  });

  const { data: results, isLoading } = useQuery({
    queryKey: ["eval-results", projectId, runId],
    queryFn: () => fetchResults(projectId!, runId!),
    enabled: !!projectId && !!runId,
  });

  const [search, setSearch] = React.useState("");
  const [filterPassed, setFilterPassed] = React.useState<string>("all");
  const [sortConfig, setSortConfig] = React.useState<{ key: string; direction: "asc" | "desc" }>({
    key: "gradedAt",
    direction: "desc",
  });

  const filteredResults =
    results?.filter((r) => {
      const matchesSearch = JSON.stringify(r.details).toLowerCase().includes(search.toLowerCase());
      const matchesPassed =
        filterPassed === "all" ||
        (filterPassed === "passed" && r.passed === true) ||
        (filterPassed === "failed" && r.passed === false) ||
        (filterPassed === "pending" && r.passed === null);
      return matchesSearch && matchesPassed;
    }) ?? [];

  const sortedResults = [...filteredResults].sort((a, b) => {
    const aVal = a[sortConfig.key as keyof typeof a];
    const bVal = b[sortConfig.key as keyof typeof b];
    if (aVal == null && bVal == null) return 0;
    if (aVal == null) return sortConfig.direction === "asc" ? -1 : 1;
    if (bVal == null) return sortConfig.direction === "asc" ? 1 : -1;
    if (aVal < bVal) return sortConfig.direction === "asc" ? -1 : 1;
    if (aVal > bVal) return sortConfig.direction === "asc" ? 1 : -1;
    return 0;
  });

  const handleSort = (key: string) => {
    setSortConfig((prev) => ({
      key,
      direction: prev.key === key && prev.direction === "asc" ? "desc" : "asc",
    }));
  };

  if (runLoading) {
    return (
      <div className="flex flex-col gap-6">
        <PageHeader title="Loading..." headingLevel={headingLevel} />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title={run?.name ?? "Results"}
        headingLevel={headingLevel}
        description={`Eval run: ${run?.environment ?? ""} — ${run?.trigger ?? ""}`}
        actions={
          <Button variant="outline">
            <Download className="mr-2 h-4 w-4" /> Export CSV
          </Button>
        }
      />

      <div className="grid gap-4 sm:grid-cols-4">
        <Card>
          <CardContent className="p-4">
            <dt className="text-xs font-medium text-muted-foreground">Total items</dt>
            <dd className="mt-1 text-2xl font-semibold tabular-nums">{run?.totalItems ?? 0}</dd>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <dt className="text-xs font-medium text-muted-foreground">Completed</dt>
            <dd className="mt-1 text-2xl font-semibold tabular-nums">{run?.completedItems ?? 0}</dd>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <dt className="text-xs font-medium text-muted-foreground">Passed</dt>
            <dd className="mt-1 text-2xl font-semibold tabular-nums text-green-600">
              {results?.filter((r) => r.passed === true).length ?? 0}
            </dd>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <dt className="text-xs font-medium text-muted-foreground">Failed</dt>
            <dd className="mt-1 text-2xl font-semibold tabular-nums text-red-600">
              {results?.filter((r) => r.passed === false).length ?? 0}
            </dd>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardContent className="p-6">
          <div className="flex flex-col sm:flex-row gap-4 mb-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search results..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-10"
              />
            </div>
            <Filter className="mr-2 h-4 w-4" />
            <Select value={filterPassed} onValueChange={setFilterPassed}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="All results" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All results</SelectItem>
                <SelectItem value="passed">Passed</SelectItem>
                <SelectItem value="failed">Failed</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full border-collapse text-sm">
              <caption className="sr-only">Results</caption>
              <thead>
                <tr className="border-b">
                  <th
                    scope="col"
                    className="h-10 px-3 text-left text-[0.8125rem] font-medium whitespace-nowrap text-muted-foreground first:pl-5 last:pr-5 w-[15%]"
                  >
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-auto p-0 text-left font-medium text-foreground hover:text-foreground"
                      onClick={() => handleSort("datasetItemId")}
                    >
                      Item
                      {sortConfig.key === "datasetItemId" &&
                        (sortConfig.direction === "asc" ? (
                          <ChevronDown className="ml-1 h-3 w-3" />
                        ) : (
                          <ChevronDown className="ml-1 h-3 w-3 rotate-180" />
                        ))}
                    </Button>
                  </th>
                  <th
                    scope="col"
                    className="h-10 px-3 text-left text-[0.8125rem] font-medium whitespace-nowrap text-muted-foreground first:pl-5 last:pr-5 w-[15%]"
                  >
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-auto p-0 text-left font-medium text-foreground hover:text-foreground"
                      onClick={() => handleSort("criterionId")}
                    >
                      Criterion
                      {sortConfig.key === "criterionId" &&
                        (sortConfig.direction === "asc" ? (
                          <ChevronDown className="ml-1 h-3 w-3" />
                        ) : (
                          <ChevronDown className="ml-1 h-3 w-3 rotate-180" />
                        ))}
                    </Button>
                  </th>
                  <th
                    scope="col"
                    className="h-10 px-3 text-left text-[0.8125rem] font-medium whitespace-nowrap text-muted-foreground first:pl-5 last:pr-5 w-[10%]"
                  >
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-auto p-0 text-left font-medium text-foreground hover:text-foreground"
                      onClick={() => handleSort("score")}
                    >
                      Score
                      {sortConfig.key === "score" &&
                        (sortConfig.direction === "asc" ? (
                          <ChevronDown className="ml-1 h-3 w-3" />
                        ) : (
                          <ChevronDown className="ml-1 h-3 w-3 rotate-180" />
                        ))}
                    </Button>
                  </th>
                  <th
                    scope="col"
                    className="h-10 px-3 text-left text-[0.8125rem] font-medium whitespace-nowrap text-muted-foreground first:pl-5 last:pr-5 w-[12%]"
                  >
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-auto p-0 text-left font-medium text-foreground hover:text-foreground"
                      onClick={() => handleSort("passed")}
                    >
                      Status
                      {sortConfig.key === "passed" &&
                        (sortConfig.direction === "asc" ? (
                          <ChevronDown className="ml-1 h-3 w-3" />
                        ) : (
                          <ChevronDown className="ml-1 h-3 w-3 rotate-180" />
                        ))}
                    </Button>
                  </th>
                  <th
                    scope="col"
                    className="h-10 px-3 text-left text-[0.8125rem] font-medium whitespace-nowrap text-muted-foreground first:pl-5 last:pr-5 w-[28%]"
                  >
                    Details
                  </th>
                  <th
                    scope="col"
                    className="h-10 px-3 text-left text-[0.8125rem] font-medium whitespace-nowrap text-muted-foreground first:pl-5 last:pr-5 w-[20%]"
                  >
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-auto p-0 text-left font-medium text-foreground hover:text-foreground"
                      onClick={() => handleSort("gradedAt")}
                    >
                      Graded
                      {sortConfig.key === "gradedAt" &&
                        (sortConfig.direction === "asc" ? (
                          <ChevronDown className="ml-1 h-3 w-3" />
                        ) : (
                          <ChevronDown className="ml-1 h-3 w-3 rotate-180" />
                        ))}
                    </Button>
                  </th>
                </tr>
              </thead>
              <tbody>
                {isLoading ? (
                  <tr>
                    <td colSpan={6} className="text-center py-8 text-muted-foreground">
                      Loading results...
                    </td>
                  </tr>
                ) : sortedResults.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="text-center py-8">
                      <p className="font-medium">No results yet</p>
                      <p className="mx-auto mt-1 max-w-md text-sm text-muted-foreground">
                        Results appear here as the eval run processes dataset items.
                      </p>
                    </td>
                  </tr>
                ) : (
                  sortedResults.map((result) => <ResultRow key={result.id} result={result} />)
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
